<script setup lang="ts">
/**
 * The porthole iris: a riveted brass ship's-porthole ring closes in from the
 * screen edges to a point, the picture goes to deep water, and the
 * destination's name pops onto the kit's enamel title plate at the centre;
 * opening runs it backwards onto the new scene (life-layer.md §2, "Porthole
 * iris between scenes"). It hides the one frame in which a heavy Three scene
 * builds, which a fade cannot do honestly.
 *
 * Owns: the cover over the picture, its timing and its tap-swallowing.
 * Must not: know which screen asked, or hold any game state.
 *
 * Web Animations, not gsap, on purpose: the first frame of the next scene is
 * expensive and gsap's lag smoothing would stretch the iris across it. Only
 * transform and opacity move, so the compositor keeps it turning through that
 * frame. The hole is not clipped or masked (both repaint a full-screen layer
 * every frame): it is a scaled ring whose huge box-shadow is the dark outside.
 *
 * Timings are the spec's (life-layer.md §2/§3 P0; Tankodromo's measured
 * shutter): 200 ms in, a hold of at least 90 ms, 280 ms out, and a 700 ms
 * fail-safe so a screen that never answers cannot leave the player under it.
 *
 * Mounted once in `App.vue`; screens reach it through `tarp.ts`.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';

import { playCue } from '@/audio/sfx';
import { POP, reducedMotion } from '@/engine/motion';

import { registerTarp } from './tarp';

const CLOSE_MS = 200;
const CLOSE_EASE = 'cubic-bezier(0.55, 0, 1, 0.45)';
const OPEN_MS = 280;
const OPEN_EASE = 'cubic-bezier(0, 0.55, 0.45, 1)';
const TAG_IN_MS = 260;
const TAG_IN_DELAY_MS = 140;
const TAG_OUT_MS = 160;
/** The tag swells as it goes, so it leaves toward the player. */
const TAG_OUT_SCALE = 1.25;
const HOLD_MIN_MS = 90;
const FAIL_SAFE_MS = 700;

/**
 * Smallest ring scale: the porthole is a pinhole (about 4% of its full size,
 * ~45 px at 1440 wide) just before the water takes it. It must stay above
 * what the box-shadow can still cover, see `--spread` below.
 */
const IRIS_MIN_SCALE = 0.04;
/** The water floods in over the second half of the close, and drains over the first part of the open. */
const WATER_IN_DELAY_MS = 100;
const WATER_OUT_MS = 120;

/** Brass studs round the ring; 48 reads as a porthole at every size. */
const RIVET_COUNT = 48;
/** The ring is drawn in a 1000-unit box (viewBox -500..500). */
const RING_MID_R = 484;
const RING_LENGTH = 2 * Math.PI * RING_MID_R;
/** `stroke-dasharray` with a zero dash and a round cap draws a dot per period. */
const RIVET_DASH = `0 ${(RING_LENGTH / RIVET_COUNT).toFixed(2)}`;

type State = 'open' | 'closing' | 'closed' | 'opening';

const root = ref<HTMLElement | null>(null);
const iris = ref<HTMLElement | null>(null);
const water = ref<HTMLElement | null>(null);
const tag = ref<HTMLElement | null>(null);
const name = ref('');

let state: State = 'open';
let closedAt = 0;
let failSafe = 0;
let running: Animation[] = [];
/** Resolved by the close in flight; `open` waits on it so the two never overlap. */
let closing: Promise<void> = Promise.resolve();

const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

function play(el: HTMLElement | null, frames: Keyframe[], options: KeyframeAnimationOptions): Promise<void> {
  if (!el) return Promise.resolve();
  const a = el.animate(frames, { fill: 'both', ...options });
  running.push(a);
  return a.finished.then(
    () => undefined,
    () => undefined, // cancelled by a newer call: the newer call owns the picture
  );
}

function stopRunning(): void {
  for (const a of running) a.cancel();
  running = [];
}

function show(visible: boolean): void {
  if (root.value) root.value.hidden = !visible;
}

const scaled = (s: number): string => `scale(${s})`;

function close(label: string): Promise<void> {
  if (reducedMotion() || state === 'closing' || state === 'closed') return closing;
  window.clearTimeout(failSafe);
  stopRunning();
  name.value = label;
  state = 'closing';
  playCue('tarp.drop');
  show(true);
  failSafe = window.setTimeout(() => void open(), FAIL_SAFE_MS);
  closing = Promise.all([
    play(iris.value, [{ transform: scaled(1) }, { transform: scaled(IRIS_MIN_SCALE) }], { duration: CLOSE_MS, easing: CLOSE_EASE }),
    play(water.value, [{ opacity: 0 }, { opacity: 1 }], { duration: CLOSE_MS - WATER_IN_DELAY_MS, delay: WATER_IN_DELAY_MS, easing: 'ease-in' }),
    play(
      tag.value,
      [
        { transform: 'scale(0.3) rotate(-25deg)', opacity: 0 },
        { transform: 'scale(1) rotate(0)', opacity: 1 },
      ],
      { duration: TAG_IN_MS, delay: TAG_IN_DELAY_MS, easing: POP },
    ),
  ]).then(() => {
    if (state !== 'closing') return;
    state = 'closed';
    closedAt = performance.now();
  });
  return closing;
}

async function open(): Promise<void> {
  if (state === 'open' || state === 'opening') return;
  window.clearTimeout(failSafe);
  await closing;
  // The tag has popped in and the picture has been hidden for long enough to
  // be read as a cut rather than a flicker.
  await sleep(Math.max(0, closedAt + HOLD_MIN_MS - performance.now()));
  if (state !== 'closed') return;
  state = 'opening';
  playCue('tarp.lift');
  stopRunning();
  await Promise.all([
    play(iris.value, [{ transform: scaled(IRIS_MIN_SCALE) }, { transform: scaled(1) }], { duration: OPEN_MS, easing: OPEN_EASE }),
    play(water.value, [{ opacity: 1 }, { opacity: 0 }], { duration: WATER_OUT_MS, easing: 'ease-out' }),
    play(
      tag.value,
      [
        { transform: 'scale(1) rotate(0)', opacity: 1 },
        { transform: `scale(${TAG_OUT_SCALE}) rotate(0)`, opacity: 0 },
      ],
      { duration: TAG_OUT_MS, easing: 'ease-out' },
    ),
  ]);
  if (state !== 'opening') return;
  stopRunning();
  show(false);
  state = 'open';
}

onMounted(() => {
  show(false);
  registerTarp({ close, open, isClosed: () => state === 'closing' || state === 'closed' });
});

onBeforeUnmount(() => {
  window.clearTimeout(failSafe);
  stopRunning();
  registerTarp(null);
});
</script>

<template>
  <div ref="root" class="tarp" hidden aria-hidden="true" data-test="tarp">
    <div ref="iris" class="iris">
      <svg class="ring" viewBox="-500 -500 1000 1000" focusable="false">
        <defs>
          <linearGradient id="tarp-brass" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stop-color="#ffe08a" />
            <stop offset="0.45" stop-color="#e0b04a" />
            <stop offset="1" stop-color="#6b4a12" />
          </linearGradient>
        </defs>
        <circle r="502" fill="none" stroke="#0d141c" stroke-width="8" />
        <circle :r="RING_MID_R" fill="none" stroke="url(#tarp-brass)" stroke-width="40" />
        <circle r="462" fill="none" stroke="#0d141c" stroke-width="8" />
        <circle :r="RING_MID_R" fill="none" stroke="#8a5a14" stroke-width="22" stroke-linecap="round" :stroke-dasharray="RIVET_DASH" />
        <circle :r="RING_MID_R" fill="none" stroke="#ffe08a" stroke-width="13" stroke-linecap="round" :stroke-dasharray="RIVET_DASH" />
      </svg>
    </div>
    <div ref="water" class="water"></div>
    <div ref="tag" class="tag k-header">
      <span class="tag-name k-title">{{ name }}</span>
    </div>
  </div>
</template>

<style scoped>
/* Above every panel (the settings sheet is 40), below the toasts (60). */
.tarp {
  position: fixed;
  inset: 0;
  z-index: 50;
  overflow: hidden;
  /* It swallows taps while down: nothing underneath may be pressed mid-cut. */
  touch-action: none;
}

.tarp[hidden] {
  display: none;
}

/*
 * The ring's box is 1.5 screen-lengths across (`vmax` = the longer side), so
 * the hole at full size clears every corner. The dark outside it is a
 * box-shadow spread of 1500 vmax: it still covers the screen at a scale
 * of 0.001, far below IRIS_MIN_SCALE, so only the ring has to scale.
 */
.iris {
  --spread: 1500vmax;
  position: absolute;
  left: 50%;
  top: 50%;
  width: 150vmax;
  height: 150vmax;
  margin: -75vmax 0 0 -75vmax;
  border-radius: 50%;
  box-shadow: 0 0 0 var(--spread) #0f1820;
  will-change: transform;
}

.ring {
  display: block;
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 0 6px rgba(0, 0, 0, 0.6));
}

/* What is left once the hole is a pinhole: deep harbour water, lit from the
   plate's side so the centre is not a flat slab. */
.water {
  position: absolute;
  inset: 0;
  opacity: 0;
  background:
    radial-gradient(ellipse at 50% 45%, #1f4560 0%, #12283a 45%, #0a141d 100%);
}

.tag {
  position: absolute;
  left: 50%;
  top: 50%;
  min-width: 18rem;
  translate: -50% -50%;
  filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.55));
  opacity: 0;
}

.tag-name {
  font-size: clamp(1.4rem, 1rem + 1.4vw, 2.1rem);
  text-transform: uppercase;
  white-space: nowrap;
}
</style>
